import reactUpdate from 'immutability-helper';
import {useMemo, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {
  buildReactionFromComponents,
  BuildReactionFromComponentsResponse,
  ReactionComponentInput,
} from 'chemistry/ChemistryDAO';
import makeCancelable from 'lib/makeCancelable';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getMoleculeVersionIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  SyncedReactionComponent,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

// Backward compatibility: older persisted tables store syncedComponents as plain molecule ID
// strings. Normalize to tagged union format so isEqual comparisons work correctly.
function normalizeSyncedComponents(
  components: SyncedReactionComponents | undefined
): SyncedReactionComponents | undefined {
  if (components == null) {
    return undefined;
  }
  return {
    [ChemistryReactionRole.REACTANT]: components[ChemistryReactionRole.REACTANT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
    [ChemistryReactionRole.PRODUCT]: components[ChemistryReactionRole.PRODUCT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
  };
}

/**
 * Drop `versionId` from a table-side molecule component when the corresponding diagram-side
 * component hasn't recorded a version yet (migration case -- older tables/diagrams predate
 * version tracking). This function itself is pure. The caller (below) persists the newly-known
 * version immediately, as a one-time backfill with no diagram rebuild, so that a *future*
 * re-versioning of the same molecule is detected as a genuine mismatch instead of being silently
 * dropped again forever. Only a genuine mismatch (both sides recorded, and different) should
 * trigger a rebuild.
 */
function dropUnrecordedVersionMismatches(
  tableComponents: SyncedReactionComponents,
  diagramComponents: SyncedReactionComponents
): SyncedReactionComponents {
  const dropForRole = (role: keyof SyncedReactionComponents): Array<SyncedReactionComponent | string> => {
    const diagramList = diagramComponents[role];
    return tableComponents[role].map((component, index) => {
      if (typeof component === 'string' || component.type !== 'molecule' || component.versionId == null) {
        return component;
      }
      const diagramComponent = diagramList[index];
      const diagramVersionId =
        typeof diagramComponent === 'object' && diagramComponent.type === 'molecule'
          ? diagramComponent.versionId
          : undefined;
      if (diagramVersionId == null) {
        const {versionId, ...withoutVersion} = component;
        return withoutVersion;
      }
      return component;
    });
  };
  return {
    [ChemistryReactionRole.REACTANT]: dropForRole(ChemistryReactionRole.REACTANT),
    [ChemistryReactionRole.PRODUCT]: dropForRole(ChemistryReactionRole.PRODUCT),
  };
}

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex, versionColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT) {
      if (moleculeId) {
        // versionId comes straight from the evaluated Molecule Version column (computedTable),
        // so it reflects whatever Compound currently resolves to -- typed, lot-auto-linked,
        // formula, or pasted -- with no extra per-mechanism handling here.
        const versionId =
          versionColumnIndex !== -1
            ? (getMoleculeVersionIdFromEvaluatedCell(evaluatedRow[versionColumnIndex]) ?? undefined)
            : undefined;
        components[role].push({type: 'molecule', id: moleculeId, ...(versionId != null ? {versionId} : {})});
      } else {
        const smiles = rows[i]?.metadata?.canonicalSmiles;
        if (smiles) {
          components[role].push({type: 'smiles', smiles});
        }
      }
    }
  }

  return components;
}

function rowToApiComponent(
  row: StoichiometryTableRow | undefined,
  evaluatedRow: EvaluatedTable[number],
  compoundColumnIndex: number,
  ignoreDrawnMolfile: boolean
): ReactionComponentInput | null {
  if (!ignoreDrawnMolfile && row?.metadata?.drawnMolfile) {
    return {type: 'molfile', molfile: row.metadata.drawnMolfile};
  }
  const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
  if (moleculeId) {
    return {type: 'molecule', moleculeId};
  }
  const smiles = row?.metadata?.canonicalSmiles;
  if (smiles) {
    return {type: 'smiles', smiles};
  }
  return null;
}

/**
 * A drawn molfile predates version tracking and always depicts whatever the molecule looked like
 * at draw time. If the molecule has since been re-versioned (recorded and live versions both
 * known, and different), that drawing is stale and rowToApiComponent should resolve the molecule
 * fresh instead of trusting it -- matched by molecule id rather than array position, since a row
 * can contribute no entry at all to diagramComponents (e.g. drawnMolfile with no resolvable
 * molecule or smiles).
 */
function hasReVersionedSinceLastDiagramBuild(
  evaluatedRow: EvaluatedTable[number],
  compoundColumnIndex: number,
  versionColumnIndex: number,
  role: ChemistryReactionRole.REACTANT | ChemistryReactionRole.PRODUCT,
  diagramComponents: SyncedReactionComponents | undefined
): boolean {
  if (versionColumnIndex === -1 || diagramComponents == null) {
    return false;
  }
  const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
  const liveVersionId = getMoleculeVersionIdFromEvaluatedCell(evaluatedRow[versionColumnIndex]);
  if (moleculeId == null || liveVersionId == null) {
    return false;
  }
  const recordedComponent = diagramComponents[role].find(
    (component) =>
      typeof component === 'object' && component.type === 'molecule' && component.id === moleculeId
  );
  const recordedVersionId =
    typeof recordedComponent === 'object' && recordedComponent?.type === 'molecule'
      ? recordedComponent.versionId
      : undefined;
  return recordedVersionId != null && recordedVersionId !== liveVersionId;
}

function buildApiComponentsFromRows(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>,
  diagramComponents: SyncedReactionComponents | undefined
): {reactants: Array<ReactionComponentInput>; products: Array<ReactionComponentInput>} {
  const {compoundColumnIndex, roleColumnIndex, versionColumnIndex} = getColumnIndices(columns);
  const reactants: Array<ReactionComponentInput> = [];
  const products: Array<ReactionComponentInput> = [];

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);
    if (role !== ChemistryReactionRole.REACTANT && role !== ChemistryReactionRole.PRODUCT) {
      continue;
    }
    const ignoreDrawnMolfile = hasReVersionedSinceLastDiagramBuild(
      evaluatedRow,
      compoundColumnIndex,
      versionColumnIndex,
      role,
      diagramComponents
    );
    const apiComponent = rowToApiComponent(rows[i], evaluatedRow, compoundColumnIndex, ignoreDrawnMolfile);
    if (apiComponent != null) {
      (role === ChemistryReactionRole.REACTANT ? reactants : products).push(apiComponent);
    }
  }

  return {reactants, products};
}

interface PrepareAndBuildResult {
  response: BuildReactionFromComponentsResponse;
  components: SyncedReactionComponents;
}

async function prepareAndBuildUpdatedReactionDiagram(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  diagramComponents: SyncedReactionComponents | undefined
): Promise<PrepareAndBuildResult> {
  // syncedComponents tracks component identity (molecule ID or SMILES) for staleness detection.
  // buildApiComponentsFromRows separately prefers drawnMolfile in the API payload to preserve
  // user-drawn 2D orientation without resolving entity molfiles on the frontend -- unless the
  // molecule has been re-versioned since diagramComponents was last recorded, in which case the
  // drawing is stale and buildApiComponentsFromRows resolves the molecule fresh instead.
  const components = extractCurrentTableComponents(
    computedTable,
    structuredTable.columns,
    structuredTable.rows
  );
  const {reactants, products} = buildApiComponentsFromRows(
    computedTable,
    structuredTable.columns,
    structuredTable.rows,
    diagramComponents
  );
  const response = await buildReactionFromComponents(reactants, products);
  return {response, components};
}

/**
 * Syncs reaction components from a stoichiometry table to a reaction scheme file.
 *
 * Monitors reactant and product molecules in the table and automatically updates
 * the reaction scheme when components change. Calls the chemistry service to build
 * a new reaction diagram and invokes `onUpdate` with both the updated NoteLinkedFile
 * (carrying the new external file) and the updated table (with refreshed syncedComponents).
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when a new diagram is generated
 * @returns true if a diagram update is in progress, false otherwise
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): boolean {
  const diagramReactionComponents = useMemo(
    () => normalizeSyncedComponents(structuredTable.metadata.syncedComponents),
    [structuredTable.metadata.syncedComponents]
  );
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns, structuredTable.rows),
    [computedTable, structuredTable.columns, structuredTable.rows]
  );
  // Count in-flight builds rather than a single boolean so overlapping runs (a build canceled
  // on cleanup that resolves after its replacement started) can't clobber each other's spinner state.
  const [inFlightCount, setInFlightCount] = useState(0);

  useEffect(() => {
    if (diagramReactionComponents != null) {
      // Compare with unrecorded-version mismatches dropped first, so a migration-case table
      // (versionId newly available but never recorded on the diagram side) doesn't spuriously
      // trigger a full rebuild -- only a genuine version *change* should.
      // Use deep equality check since the tableReactionComponents array is not completely stable.
      const tableComponentsWithUnrecordedVersionsDropped = dropUnrecordedVersionMismatches(
        tableReactionComponents,
        diagramReactionComponents
      );
      if (isEqual(tableComponentsWithUnrecordedVersionsDropped, diagramReactionComponents)) {
        // Content matches, but if that's only true once unrecorded versions are dropped, the
        // diagram side has never recorded a version baseline for one or more components (either a
        // pre-existing diagram from before version tracking, or a producer -- e.g. the sketcher --
        // that doesn't record versionId at all). Persist the table's versions as that baseline now,
        // without a diagram rebuild, so a future re-versioning of the same molecule is detected as
        // a genuine mismatch instead of being silently dropped again forever.
        if (!isEqual(tableReactionComponents, diagramReactionComponents)) {
          onUpdate(
            reactionSchemeFile,
            reactUpdate(structuredTable, {
              metadata: {$merge: {syncedComponents: tableReactionComponents}},
            })
          );
        }
        return;
      }
    }

    // Tie the result to the run that started it. If the table state changes while the build is
    // in flight (e.g. the sketcher is submitted), canceling on cleanup discards this run's result
    // rather than reverting the sketcher's file.
    const {promise, cancel} = makeCancelable(
      prepareAndBuildUpdatedReactionDiagram(structuredTable, computedTable, diagramReactionComponents)
    );
    setInFlightCount((count) => count + 1);

    (async (): Promise<void> => {
      try {
        const {response, components} = await promise;
        const {externalFile, noteLinkedFile} = response;
        if (
          reactionSchemeFile.externalFile != null &&
          externalFile.id === reactionSchemeFile.externalFile.id
        ) {
          return;
        }
        onUpdate(
          serverNoteLinkedFileToClientNoteLinkedFile(noteLinkedFile),
          reactUpdate(structuredTable, {
            metadata: {$merge: {syncedComponents: components}},
          })
        );
      } catch (err) {
        // A run superseded via cleanup rejects with isCanceled; that's expected. Let any real
        // failure surface instead of silently leaving the diagram out of sync.
        if (!err?.isCanceled) {
          throw err;
        }
      } finally {
        setInFlightCount((count) => count - 1);
      }
    })();

    return cancel;
  }, [
    tableReactionComponents,
    diagramReactionComponents,
    structuredTable,
    computedTable,
    onUpdate,
    reactionSchemeFile,
  ]);

  return inFlightCount > 0;
}
